import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useWallet } from '@/hooks/useWallet';
import { useAuth } from '@/hooks/useAuth';
import { useFavorites } from '@/hooks/useFavorites';
import { NFT, TraitIndex } from '@/lib/types';
import { getAnimatedThumbnailUrl, fetchIPFSMetadata } from '@/lib/ipfs';
import { fetchNFTDetails as fetchNFTFromTzkt, getOrBuildTraitIndex, fetchMarketplaceListings } from '@/lib/tzkt';
import { getCollectionByAddress, CollectionInfo } from '@/lib/collections';
import { useAppNetwork } from '@/hooks/useAppNetwork';
import { isEmoji } from '@/components/EmojiTraitFilter';
import { HeartButton } from '@/components/ui/HeartButton';
import { AuthToast } from '@/components/ui/AuthToast';
import { TransactionToast, TransactionStatus } from '@/components/ui/TransactionToast';
import { getFavoriteStatus } from '@/lib/favorites';

export default function NFTDetailPage() {
  const { tokenAddress, tokenId } = useParams<{ tokenAddress: string; tokenId: string }>();
  const { wallet, isConnected, address, tezos, connect, isLoading: isWalletLoading } = useWallet();
  const { isAuthenticated, authenticate, isAuthenticating } = useAuth(wallet, address);
  const { toggleFavorite, getStatus, isLoading: isFavoriteLoading, updateStatus } = useFavorites();
  const { network: appNetwork } = useAppNetwork();

  const [nft, setNft] = useState<NFT | null>(null);
  const [collection, setCollection] = useState<CollectionInfo | null>(null);
  const [traitIndex, setTraitIndex] = useState<TraitIndex | null>(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [showAuthToast, setShowAuthToast] = useState(false);
  const [authSuccess, setAuthSuccess] = useState(false);

  // Transaction toast state
  const [txStatus, setTxStatus] = useState<TransactionStatus>('idle');
  const [txMessage, setTxMessage] = useState('');

  // Listing state (for when owner views their NFT)
  const [showListingForm, setShowListingForm] = useState(false);
  const [listingPrice, setListingPrice] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [isListing, setIsListing] = useState(false);
  const [listingStep, setListingStep] = useState<'approve' | 'list'>('approve');

  useEffect(() => {
    if (tokenAddress) {
      const collectionInfo = getCollectionByAddress(tokenAddress);
      setCollection(collectionInfo || null);

      // Load trait index for rarity calculations
      if (collectionInfo) {
        getOrBuildTraitIndex(tokenAddress).then(setTraitIndex);
      }
    }
  }, [tokenAddress]);

  useEffect(() => {
    fetchNFTDetails();
  }, [tokenAddress, tokenId, appNetwork]);

  // Load favorite status when NFT loads
  useEffect(() => {
    if (tokenAddress && tokenId) {
      getFavoriteStatus(tokenAddress, tokenId)
        .then((status) => {
          updateStatus(tokenAddress, tokenId, status);
        })
        .catch(() => {
          // Ignore errors - just won't show favorite status
        });
    }
  }, [tokenAddress, tokenId, updateStatus]);

  // Handle favorite click
  const handleFavoriteClick = useCallback(async () => {
    if (!tokenAddress || !tokenId) return;

    if (!address) {
      setShowAuthToast(true);
      return;
    }

    if (!isAuthenticated) {
      setShowAuthToast(true);
      return;
    }

    try {
      await toggleFavorite(tokenAddress, tokenId, true, async () => {});
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  }, [tokenAddress, tokenId, address, isAuthenticated, toggleFavorite]);

  // Handle sign in from toast
  const handleSignIn = useCallback(async () => {
    // If no wallet connected, connect first
    if (!address) {
      await connect();
      // After connect, address will update via state
      // The useEffect below will handle authentication once wallet is connected
      return;
    }

    const success = await authenticate();
    if (success) {
      setAuthSuccess(true);

      // Toggle the favorite
      if (tokenAddress && tokenId) {
        try {
          await toggleFavorite(tokenAddress, tokenId, true, async () => {});
        } catch (err) {
          console.error('Failed to toggle favorite:', err);
        }
      }

      setTimeout(() => {
        setShowAuthToast(false);
        setAuthSuccess(false);
      }, 2000);
    }
  }, [address, connect, authenticate, tokenAddress, tokenId, toggleFavorite]);

  // Auto-authenticate after wallet connects if we have the toast open
  // Use a ref to track if we've already started auth to prevent loops
  const hasStartedAuth = useRef(false);

  useEffect(() => {
    // Reset the ref when toast is hidden
    if (!showAuthToast) {
      hasStartedAuth.current = false;
      return;
    }

    // Only auto-trigger if:
    // 1. Toast is showing
    // 2. Wallet just connected (address exists)
    // 3. Not already authenticated
    // 4. Not currently authenticating
    // 5. Haven't already started auth this session
    if (address && !isAuthenticated && !isAuthenticating && !hasStartedAuth.current) {
      hasStartedAuth.current = true;
      handleSignIn();
    }
  }, [address, isAuthenticated, showAuthToast, isAuthenticating, handleSignIn]);

  // Handle dismiss toast
  const handleDismissAuthToast = useCallback(() => {
    setShowAuthToast(false);
  }, []);

  // Check if current user is the owner
  const isOwner = nft && address && nft.owner.toLowerCase() === address.toLowerCase();

  // Handle listing approval
  const handleApprove = async () => {
    if (!nft || !tezos || !address) return;

    const marketplaceContract = import.meta.env.VITE_MARKETPLACE_CONTRACT;
    if (!marketplaceContract) {
      alert('Marketplace contract not configured');
      return;
    }

    setIsApproving(true);
    setTxStatus('pending');
    setTxMessage('Approve marketplace to transfer your NFT');

    try {
      // Use wallet API for Beacon wallet transactions
      const nftContract = await tezos.wallet.at(nft.tokenAddress);

      // FA2 update_operators entrypoint
      const op = await nftContract.methods.update_operators([
        {
          add_operator: {
            owner: address,
            operator: marketplaceContract,
            token_id: nft.tokenId,
          },
        },
      ]).send();

      setTxStatus('confirming');
      setTxMessage('Waiting for approval confirmation...');

      await op.confirmation();
      setTxStatus('success');
      setTxMessage('Marketplace approved! Now list your NFT.');
      setListingStep('list');
    } catch (error: any) {
      console.error('Failed to approve:', error);
      setTxStatus('error');
      setTxMessage(error?.message || 'Failed to approve. Please try again.');
    } finally {
      setIsApproving(false);
    }
  };

  // Handle listing submission
  const handleList = async () => {
    if (!nft || !tezos || !listingPrice) return;

    const marketplaceContract = import.meta.env.VITE_MARKETPLACE_CONTRACT;
    if (!marketplaceContract) {
      alert('Marketplace contract not configured');
      return;
    }

    setIsListing(true);
    setTxStatus('pending');
    setTxMessage(`List NFT for ${listingPrice} ꜩ`);

    try {
      // Use wallet API for Beacon wallet transactions
      const contract = await tezos.wallet.at(marketplaceContract);

      const op = await contract.methods.list_nft(
        nft.tokenAddress,
        nft.tokenId,
        Math.floor(parseFloat(listingPrice) * 1000000) // Convert to mutez
      ).send();

      setTxStatus('confirming');
      setTxMessage('Waiting for listing confirmation...');

      await op.confirmation();

      setTxStatus('success');
      setTxMessage('NFT listed successfully!');
      setShowListingForm(false);
      setListingPrice('');
      setListingStep('approve');
      fetchNFTDetails(); // Refresh to show listing
    } catch (error: any) {
      console.error('Failed to list NFT:', error);
      setTxStatus('error');
      setTxMessage(error?.message || 'Failed to list NFT. Make sure you have approved the marketplace first.');
    } finally {
      setIsListing(false);
    }
  };

  const fetchNFTDetails = async () => {
    if (!tokenAddress || !tokenId) {
      setLoading(false);
      return;
    }

    const isSandbox = appNetwork === 'sandbox';

    try {
      if (isSandbox) {
        // Sandbox mode: fetch directly from contract via Taquito
        const { TezosToolkit } = await import('@taquito/taquito');
        // Sandbox RPC: in browser use proxy, fallback to env var or mainnet in production
        const sandboxFallback = import.meta.env.PROD ? 'https://mainnet.api.tez.ie' : 'http://localhost:20000';
        const rpcUrl = typeof window !== 'undefined' ? '/rpc' : (import.meta.env.VITE_RPC_URL || sandboxFallback);
        const tezosClient = new TezosToolkit(rpcUrl);
        const contract = await tezosClient.contract.at(tokenAddress);
        const storage = await contract.storage() as {
          ledger: { get: (id: number) => Promise<string | undefined> };
          token_metadata: { get: (id: number) => Promise<{ token_id: { toNumber: () => number }; token_info: Map<string, string> } | undefined> };
        };

        const tokenIdNum = parseInt(tokenId);
        const owner = await storage.ledger.get(tokenIdNum) || '';
        const tokenMetaEntry = await storage.token_metadata.get(tokenIdNum);

        let metadata: NFT['metadata'] = {
          name: `Token #${tokenId}`,
          description: '',
          image: '',
        };

        if (tokenMetaEntry?.token_info) {
          const metadataUriHex = tokenMetaEntry.token_info.get('');
          if (metadataUriHex) {
            // Decode hex to UTF-8
            const cleanHex = metadataUriHex.startsWith('0x') ? metadataUriHex.slice(2) : metadataUriHex;
            const bytes = new Uint8Array(cleanHex.length / 2);
            for (let i = 0; i < cleanHex.length; i += 2) {
              bytes[i / 2] = parseInt(cleanHex.substring(i, i + 2), 16);
            }
            const metadataUri = new TextDecoder('utf-8').decode(bytes);

            if (metadataUri.startsWith('ipfs://')) {
              const metadataJson = await fetchIPFSMetadata(metadataUri);
              if (metadataJson) {
                metadata = {
                  name: metadataJson.name || metadata.name,
                  description: metadataJson.description || '',
                  image: metadataJson.displayUri || metadataJson.artifactUri || metadataJson.thumbnailUri || metadataJson.image || '',
                  attributes: metadataJson.attributes?.map((attr: { name: string; value: string }) => ({
                    trait_type: attr.name,
                    value: attr.value,
                  })),
                };
              }
            }
          }
        }

        // Fetch listing info for this NFT
        const listings = await fetchMarketplaceListings();
        const listing = listings.find(
          l => l.tokenAddress === tokenAddress && String(l.tokenId) === tokenId && l.active
        );

        setNft({
          tokenAddress,
          tokenId,
          owner,
          metadata,
          listing,
        });
      } else {
        // Production mode: use TzKT API for the current network
        const nftData = await fetchNFTFromTzkt(tokenAddress, tokenId, appNetwork);
        if (nftData) {
          setNft(nftData);
        }
      }
    } catch (error) {
      console.error('Failed to fetch NFT details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate trait rarity percentage
  const getTraitRarity = (traitType: string, value: string | number): number | null => {
    if (!traitIndex || !traitIndex._meta) return null;
    const traitData = traitIndex[traitType] as Record<string, number> | undefined;
    if (!traitData || typeof traitData !== 'object') return null;
    const count = traitData[String(value)];
    if (typeof count !== 'number') return null;
    return (count / traitIndex._meta.totalSupply) * 100;
  };

  // Get rarity color based on percentage
  const getRarityColor = (percentage: number): string => {
    if (percentage <= 1) return 'text-yellow-600 bg-yellow-50 border-yellow-200'; // Legendary
    if (percentage <= 5) return 'text-purple-600 bg-purple-50 border-purple-200'; // Epic
    if (percentage <= 15) return 'text-blue-600 bg-blue-50 border-blue-200'; // Rare
    if (percentage <= 30) return 'text-green-600 bg-green-50 border-green-200'; // Uncommon
    return 'text-gray-600 bg-gray-50 border-gray-200'; // Common
  };

  const handleBuy = async () => {
    if (!isConnected || !tezos || !nft?.listing) {
      alert('Please connect your wallet first');
      return;
    }

    const marketplaceContract = import.meta.env.VITE_MARKETPLACE_CONTRACT;
    if (!marketplaceContract) {
      alert('Marketplace contract not configured');
      return;
    }

    // Validate listing data
    const listingId = nft.listing.id;
    const priceInMutez = parseInt(nft.listing.price);

    console.log('Buy NFT - Listing data:', {
      listingId,
      listingIdType: typeof listingId,
      price: nft.listing.price,
      priceInMutez,
      seller: nft.listing.seller,
      marketplaceContract,
    });

    if (typeof listingId !== 'number' || isNaN(listingId)) {
      alert(`Invalid listing ID: ${listingId}`);
      return;
    }

    if (isNaN(priceInMutez) || priceInMutez <= 0) {
      alert(`Invalid price: ${nft.listing.price}`);
      return;
    }

    setBuying(true);
    setTxStatus('pending');
    setTxMessage(`Purchase NFT for ${formatPrice(nft.listing.price)}`);

    try {
      // Use wallet API for Beacon wallet transactions
      const contract = await tezos.wallet.at(marketplaceContract);
      console.log('Sending buy_nft with:', { listingId, priceInMutez });
      const op = await contract.methods.buy_nft(listingId).send({
        amount: priceInMutez,
        mutez: true,
      });

      setTxStatus('confirming');
      setTxMessage('Waiting for purchase confirmation...');

      await op.confirmation();
      setTxStatus('success');
      setTxMessage('NFT purchased successfully!');
      fetchNFTDetails();
    } catch (error: any) {
      console.error('Failed to buy NFT:', error);
      setTxStatus('error');
      setTxMessage(error?.message || 'Failed to purchase NFT. Please try again.');
    } finally {
      setBuying(false);
    }
  };

  const formatPrice = (price: string) => {
    const tez = parseInt(price) / 1000000;
    return `${tez.toFixed(2)} ꜩ`;
  };

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 8)}...${addr.slice(-6)}`;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading NFT details...</p>
        </div>
      </div>
    );
  }

  if (!nft) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">NFT Not Found</h1>
          <Link to="/" className="text-blue-600 hover:underline dark:text-blue-400">
            Back to Collections
          </Link>
        </div>
      </div>
    );
  }

  const backLink = collection ? `/collection/${collection.slug}` : '/';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6 overflow-hidden">
          <Link to="/" className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 flex-shrink-0">
            Collections
          </Link>
          {collection && (
            <>
              <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">/</span>
              <Link to={backLink} className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 flex-shrink-0">
                {collection.name}
              </Link>
            </>
          )}
          <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">/</span>
          <span className="text-gray-900 dark:text-white truncate">{nft.metadata.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image - Uses image processor for caching and optimization */}
          <div className="relative aspect-square bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg">
            {nft.metadata.image && (
              <img
                src={getAnimatedThumbnailUrl(nft.metadata.image, 512)}
                alt={nft.metadata.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${nft.tokenId}&size=600&background=random`;
                }}
              />
            )}
          </div>

          {/* Details */}
          <div className="space-y-6">
            {/* Collection badge */}
            {collection && (
              <Link
                to={backLink}
                className="inline-flex items-center gap-2 px-3 py-1 bg-white dark:bg-gray-800 rounded-full shadow-sm hover:shadow transition-shadow"
              >
                <img
                  src={collection.thumbnail}
                  alt={collection.name}
                  className="w-5 h-5 rounded-full object-cover"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{collection.name}</span>
              </Link>
            )}

            {/* Title */}
            <div className="min-w-0">
              <div className="flex items-center justify-between gap-4">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white truncate" title={nft.metadata.name}>{nft.metadata.name}</h1>
                {tokenAddress && tokenId && (
                  <HeartButton
                    isFavorited={getStatus(tokenAddress, tokenId)?.isFavorited ?? false}
                    favoritesCount={getStatus(tokenAddress, tokenId)?.favoritesCount ?? 0}
                    isLoading={isFavoriteLoading(tokenAddress, tokenId)}
                    onClick={handleFavoriteClick}
                    size="lg"
                    showCount={true}
                    className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700"
                  />
                )}
              </div>
              {nft.metadata.description && (
                <p className="text-gray-600 dark:text-gray-400 mt-2">{nft.metadata.description}</p>
              )}
            </div>

            {/* Owner & Details */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-500 dark:text-gray-400">Owner</span>
                <span className="font-mono text-sm text-gray-900 dark:text-gray-200">{formatAddress(nft.owner)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500 dark:text-gray-400">Contract</span>
                <span className="font-mono text-sm text-gray-900 dark:text-gray-200">{formatAddress(nft.tokenAddress)}</span>
              </div>
              <div className="flex justify-between items-center gap-4">
                <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">Token ID</span>
                <span
                  className="font-mono text-sm text-gray-900 dark:text-gray-200 truncate"
                  title={`#${nft.tokenId}`}
                >
                  #{nft.tokenId.length > 20 ? `${nft.tokenId.slice(0, 8)}...${nft.tokenId.slice(-8)}` : nft.tokenId}
                </span>
              </div>
            </div>

            {/* Price & Buy */}
            {nft.listing && nft.listing.active && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
                <div className="mb-4">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">Current Price</span>
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                    {formatPrice(nft.listing.price)}
                  </div>
                </div>

                {isConnected ? (
                  nft.listing.seller === address ? (
                    <p className="text-gray-500 dark:text-gray-400">You own this listing</p>
                  ) : (
                    <button
                      onClick={handleBuy}
                      disabled={buying}
                      className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors"
                    >
                      {buying ? 'Processing...' : 'Buy Now'}
                    </button>
                  )
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-center">
                    Connect your wallet to purchase
                  </p>
                )}
              </div>
            )}

            {/* List for Sale - Only show if owner and not currently listed */}
            {isOwner && (!nft.listing || !nft.listing.active) && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
                {!showListingForm ? (
                  <button
                    onClick={() => setShowListingForm(true)}
                    className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                  >
                    List for Sale
                  </button>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900 dark:text-white">List NFT for Sale</h3>
                      <button
                        onClick={() => {
                          setShowListingForm(false);
                          setListingPrice('');
                          setListingStep('approve');
                        }}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Price Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Listing Price
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={listingPrice}
                          onChange={(e) => setListingPrice(e.target.value)}
                          placeholder="0.00"
                          className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg
                            focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg
                            bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                          ꜩ
                        </span>
                      </div>
                    </div>

                    {/* Steps */}
                    <div className="space-y-3">
                      <button
                        onClick={handleApprove}
                        disabled={isApproving || listingStep === 'list'}
                        className={`w-full px-6 py-3 rounded-lg font-medium transition-all ${
                          listingStep === 'list'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-300 dark:border-green-700'
                            : 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50'
                        }`}
                      >
                        {listingStep === 'list' ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Approved
                          </span>
                        ) : isApproving ? (
                          'Check wallet to approve...'
                        ) : (
                          '1. Approve Marketplace'
                        )}
                      </button>
                      <button
                        onClick={handleList}
                        disabled={isListing || !listingPrice || listingStep === 'approve'}
                        className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors"
                      >
                        {isListing ? 'Check wallet to confirm...' : '2. List for Sale'}
                      </button>
                    </div>

                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                      First approve the marketplace to transfer your NFT, then list it for sale.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Traits with Rarity */}
            {nft.metadata.attributes && nft.metadata.attributes.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Traits</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {nft.metadata.attributes.map((attr, index) => {
                    const rarity = getTraitRarity(attr.trait_type, attr.value);
                    const colorClass = rarity !== null ? getRarityColor(rarity) : 'bg-gray-50 border-gray-200 text-gray-600';

                    return (
                      <div
                        key={index}
                        className={`rounded-lg p-3 border ${colorClass}`}
                      >
                        <div className="text-xs uppercase tracking-wide opacity-75">
                          {attr.trait_type}
                        </div>
                        <div className={`font-medium mt-1 ${isEmoji(String(attr.value)) ? 'text-3xl' : 'truncate'}`}>
                          {attr.value}
                        </div>
                        {rarity !== null && (
                          <div className="text-xs mt-1 opacity-75">
                            {rarity.toFixed(1)}% have this
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Auth Toast */}
      <AuthToast
        isVisible={showAuthToast}
        isAuthenticating={isAuthenticating}
        isSuccess={authSuccess}
        isWalletConnected={!!address}
        isWalletLoading={isWalletLoading}
        onSignIn={handleSignIn}
        onDismiss={handleDismissAuthToast}
      />

      {/* Transaction Toast */}
      <TransactionToast
        status={txStatus}
        message={txMessage}
        onDismiss={() => {
          setTxStatus('idle');
          setTxMessage('');
        }}
      />
    </div>
  );
}
